'use client'

import { createRef, useState } from 'react'
import styles from './IdentityCircle.module.scss'
import usePersonality from '@crystal-eyes/hooks/profiles/usePersonality'
import usePersonalInfo from '@crystal-eyes/hooks/profiles/usePersonalInfo'
import { circumplexPoint } from '@crystal-eyes/lib/personality_math'
import { clamp } from '@crystal-eyes/lib/math'
import useVerifiedStatus from '@crystal-eyes/hooks/profiles/context/useVerifiedStatus'
import ProfileBadge from '../../views/profiles/ProfileBadge'

interface Identity {
  id: string
}

type Props = {
  identity: Identity
  className?: string
  style?: React.CSSProperties
  x?: number
  y?: number
  circumplexWidth?: number | null
  circumplexHeight?: number | null
  r: number
  onClick?: () => void
  onMouseOver?: () => void
  onMouseOut?: () => void
  isPrediction?: boolean | null | undefined
  includeVerified?: boolean
}

export default function IdentityCircle({
  identity,
  r,
  x = 0,
  y = 0,
  circumplexWidth = null,
  circumplexHeight = null,
  className = '',
  style = {},
  onClick,
  onMouseOver,
  onMouseOut,
  isPrediction,
  includeVerified,
}: Props) {
  const { data: personality, loading: personalityLoading } = usePersonality({
    identity: identity,
  })
  const [imageError, setImageError] = useState(false)
  const [preserveAspectRatio, setPreserveAspectRatio] = useState('none')
  const { data: personalInfo } = usePersonalInfo({ identity })
  const { data: verificationData } = useVerifiedStatus({ identity })
  const isVerified = verificationData?.isCrystalUser && includeVerified
  const discType = isPrediction
    ? personality?.prediction?.discType
    : personality?.discType

  const circumplex = isPrediction
    ? personality?.prediction?.discCircumplex
    : personality?.discCircumplex
  const imageRef = createRef<SVGImageElement>()
  const [left, bottom] = circumplexPoint(
    circumplex?.degrees || 0,
    clamp(circumplex?.intensity || 0, 30, 70),
  )

  const names = (
    personalInfo?.fullName
      ? personalInfo?.fullName.split(' ')
      : [personalInfo?.firstName, personalInfo?.lastName]
  ).filter(n => n)

  let initials: string = ''
  if (names.length >= 2) {
    initials = `${names[0]![0]}${names[1]![0]}`
  } else if (names.length >= 1) {
    initials = names[0]![0]
  }

  return personalityLoading && !initials ? (
    <EmptyIdentityCircle r={r} text={''} />
  ) : (
    <IconContainer isVerified={isVerified} r={r} className={className}>
      {initials ? (
        <svg
          className={`${styles.identityCircle} ${className}`}
          viewBox={`0 0 ${r * 2} ${r * 2}`}
          width={r * 2}
          height={r * 2}
          x={
            circumplexWidth !== null ? circumplexWidth * (left / 100.0) - r : x
          }
          y={
            circumplexHeight !== null
              ? circumplexHeight * ((100.0 - bottom) / 100.0) - r
              : y
          }
          style={style}
        >
          <circle
            cx={r}
            cy={r}
            r={r}
            className={`${`CE fill-disc ${discType || 'noDisc'}`}`}
            onClick={onClick}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
          />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            fill="white"
            dy=".3em"
            style={{ fontSize: `${r * 0.9}px` }}
          >
            {initials}
          </text>
          {!imageError && personalInfo?.photoUrl && (
            <image
              href={personalInfo?.photoUrl || ''}
              width={r * 2}
              height={r * 2}
              preserveAspectRatio={preserveAspectRatio}
              clipPathUnits="objectBoundingBox"
              onClick={onClick}
              onMouseOver={onMouseOver}
              onMouseOut={onMouseOut}
              onError={_e => {
                setImageError(true)
              }}
              onLoad={() => {
                setPreserveAspectRatio('xMidYMid slice')
              }}
              ref={imageRef}
            />
          )}
        </svg>
      ) : (
        <svg
          width={r * 2}
          height={r * 2}
          viewBox="0 0 28 29"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="14" cy="14" r="14" fill="#C4C4C4" />
          <path
            d="M5.0075 19.8739C4.72764 20.0978 4.22389 20.3776 4 23.736C4 23.736 7 28.45 14 28.45C21 28.45 24.2061 23.55 24.2061 23.55C23.9822 20.0237 23.4225 20.0978 23.0867 19.8739C21.9112 19.0343 20.5679 18.4186 19.1126 18.1387L18.9447 18.3066C16.1461 20.9933 11.6683 20.9374 8.98157 18.1387C7.52628 18.4186 6.18293 19.0343 5.0075 19.8739Z"
            fill="white"
          />
          <path
            d="M19.2245 10.0787C19.2245 12.8773 17.4334 17.0193 14.019 17.0193C10.6047 17.0193 8.81357 12.8773 8.81357 10.0787C8.98149 7.22409 11.4443 5.04116 14.2989 5.20907C16.9856 5.32102 19.0566 7.44798 19.2245 10.0787Z"
            fill="white"
          />
        </svg>
      )}
      {isVerified && (
        <div style={{ position: 'absolute', top: '-0.2rem', right: '-1rem' }}>
          <ProfileBadge style={{ marginLeft: '-25%' }} type="verified" />
        </div>
      )}
    </IconContainer>
  )
}

function IconContainer({
  r,
  isVerified,
  children,
  className,
}: {
  className?: string
  r: number
  isVerified?: boolean
  children: React.ReactNode
}) {
  if (isVerified) {
    return (
      <div
        className={`${styles.identityCircle} ${className}`}
        style={{ width: r * 2, height: r * 2 }}
      >
        {children}
      </div>
    )
  } else {
    return children
  }
}

export function EmptyIdentityCircle({ r, text }: { r: number; text: string }) {
  return (
    <svg
      width={r * 2}
      height={r * 2}
      viewBox="0 0 28 29"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="14" cy="14" r="14" fill="#C4C4C4" />

      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        fill="white"
        dy=".3em"
        style={{ fontSize: `${r * 0.5}px` }}
      >
        {text}
      </text>
    </svg>
  )
}
